import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {useInitialEffect} from '../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Icon, IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';

/*
 * Props.
 */

export interface EducationTooltipContentProps {
  titleIcon?: IconName;
  title?: ReactNode;
  tooltip?: ReactNode;
  buttons?: [ReactNode] | [ReactNode, ReactNode];
  onShow?: Handler;
}

/*
 * Styles.
 */

const TooltipContentWrapper = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  size: ${fontSizes.verySmall};

  // For design
  line-height: 18px;

  padding: 13px 12px 14px;
`;

const TooltipTitle = styled.h2`
  display: flex;
  align-items: center;

  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.medium};
  margin-top: 0px;
  margin-bottom: 10px;
`;

const StyledIcon = styled(Icon)`
  margin-right: 8px;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  align-self: flex-end;
  margin-top: 16px;
`;

const SecondButtonWrapper = styled.div`
  margin-left: 12px;
`;

/*
 * Component.
 */

/**
 * @see Intended to be used as child component of {@link EducationTooltipCoordinator}
 */
export const EducationTooltipContent: FC<EducationTooltipContentProps> = ({
  titleIcon,
  title,
  tooltip,
  buttons,
  onShow,
}) => {
  useInitialEffect(() => {
    if (onShow) {
      onShow();
    }
  });
  return (
    <TooltipContentWrapper>
      {maybeRenderTitle(titleIcon, title)}
      {tooltip}
      {maybeRenderButtons(buttons)}
    </TooltipContentWrapper>
  );
};

/*
 * Helpers.
 */

function maybeRenderTitle(titleIcon: IconName | undefined, title: ReactNode | undefined) {
  if (!title) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <TooltipTitle>
          {titleIcon && <StyledIcon name={titleIcon} color={theme.greys.white} />}
          {title}
        </TooltipTitle>
      )}
    </UserThemeConsumer>
  );
}

function maybeRenderButtons(buttons: [ReactNode] | [ReactNode, ReactNode] | undefined) {
  if (!buttons) {
    return null;
  }

  const [left, right] = buttons;
  return (
    <ButtonsWrapper>
      <div>{left}</div>
      {right && <SecondButtonWrapper>{right}</SecondButtonWrapper>}
    </ButtonsWrapper>
  );
}
